
import { useParams, useNavigate } from 'react-router-dom';
import { usePet, usePets } from '../hooks/usePets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect } from 'react';
import { ArrowLeft, CalendarIcon, ChevronLeft, ChevronRight, Navigation } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
// ✅ NEW: Import dynamic species/breeds functionality
import { useSpeciesAndBreeds } from '../hooks/useSpeciesAndBreeds';
import { BreedAutocomplete } from '../components/BreedAutocomplete';
import { Species, Breed, findBreedByName } from '../services/categoryService';
import { useGeolocation } from '../hooks/useGeolocation';
import { useAuth } from '@/contexts/AuthContext';
import { useWeightRecords } from '@/hooks/useWeightRecords';

export default function EditPetPage() {
  const { t } = useTranslation();
  const { petId } = useParams<{ petId: string }>();
  const navigate = useNavigate();
  const { data: pet, isLoading } = usePet(petId);
  const { updatePet, isUpdating } = usePets();
  const { latestWeight, addRecord } = useWeightRecords(petId);
  
  // ✅ NEW: Use dynamic species and breeds
  const {
    species: speciesList,
    breeds,
    isLoadingSpecies,
    isLoadingBreeds,
    selectedSpeciesId,
    setSelectedSpeciesId,
    speciesError,
    breedsError
  } = useSpeciesAndBreeds();
  
  const [name, setName] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [selectedBreed, setSelectedBreed] = useState<Breed | null>(null);
  const [sex, setSex] = useState('not_specified');
  const [weight, setWeight] = useState('');
  const [color, setColor] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthDateObj, setBirthDateObj] = useState<Date | null>(null);
  const [isMemorial, setIsMemorial] = useState(false);
  const [deathDate, setDeathDate] = useState('');
  const [deathDateObj, setDeathDateObj] = useState<Date | null>(null);
  const [personalityTraits, setPersonalityTraits] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [dateInputType, setDateInputType] = useState<'calendar' | 'manual'>('manual');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [location, setLocation] = useState('');
  const [hasCustomLocation, setHasCustomLocation] = useState(false);
  
  // New compatibility fields
  const [energyLevel, setEnergyLevel] = useState('');
  const [sizeCategory, setSizeCategory] = useState('');
  const [isNeutered, setIsNeutered] = useState<boolean | null>(null);
  const [socializationLevel, setSocializationLevel] = useState('');
  const [vaccinationStatus, setVaccinationStatus] = useState('');
  
  // Geolocation hook
  const { detectLocation, isDetecting, error: geolocationError } = useGeolocation();
  const { user } = useAuth();
  
  // ✅ Translated sex options
  const petSexOptions = [
    { value: 'male', label: t('modals.sexOptions.male') },
    { value: 'female', label: t('modals.sexOptions.female') },
    { value: 'not_specified', label: t('modals.sexOptions.notSpecified') }
  ];
  
  // ✅ NEW: Load pet data with dynamic species/breeds support
  useEffect(() => {
    const loadPetData = async () => {
      if (pet && speciesList.length > 0) {
        setName(pet.name || '');
        
        // Find species by code
        const species = speciesList.find(s => s.code === pet.species);
        if (species) {
          setSelectedSpecies(species);
          setSelectedSpeciesId(species.id);
          
          // Prefer selecting by stored breed_id, fallback to name (supports translations)
          if (breeds.length > 0) {
            const breedId = (pet as any)?.breed_id as number | undefined;
            if (breedId) {
              const byId = breeds.find((b) => b.id === breedId);
              if (byId) {
                setSelectedBreed(byId);
              }
            } else if (pet.breed) {
              const target = pet.breed.toLowerCase();
              const byName = breeds.find(
                (b: any) => b.name.toLowerCase() === target || b.original_name?.toLowerCase() === target
              );
              if (byName) {
                setSelectedBreed(byName);
              }
            }
          }
        }
        
        setSex(pet.sex || 'not_specified');
        setWeight(latestWeight ? latestWeight.weight_kg.toString() : (pet.weight_kg ? pet.weight_kg.toString() : ''));
        setColor(pet.color || '');
        
        if (pet.birth_date) {
          const birthDateValue = new Date(pet.birth_date);
          setBirthDate(format(birthDateValue, 'yyyy-MM-dd'));
          setBirthDateObj(birthDateValue);
          setCalendarDate(birthDateValue);
        }
        
        setIsMemorial(pet.is_memorial || false);
        
        // Load death_date
        if ((pet as any).death_date) {
          const deathDateValue = new Date((pet as any).death_date + 'T00:00:00');
          setDeathDate(format(deathDateValue, 'yyyy-MM-dd'));
          setDeathDateObj(deathDateValue);
        }
        
        setPersonalityTraits(pet.personality_traits ? pet.personality_traits.join(', ') : '');
        
        if (pet.photo_url) {
          setPhotoPreview(pet.photo_url);
        }
        
        // Handle location - check if pet has custom location
        if (pet.location) {
          setLocation(pet.location);
          setHasCustomLocation(true);
        } else {
          // Use user's location as default
          setLocation(user?.user_metadata?.location || '');
          setHasCustomLocation(false);
        }
        
        // Load new compatibility fields
        setEnergyLevel((pet as any).energy_level || '');
        setSizeCategory((pet as any).size_category || '');
        setIsNeutered((pet as any).is_neutered ?? null);
        setSocializationLevel((pet as any).socialization_level || '');
        setVaccinationStatus((pet as any).vaccination_status || '');
      }
    };
    
    loadPetData();
  }, [pet, speciesList, breeds, latestWeight]);
  
  // Maximum file size: 50MB
  const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size (50MB max)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        toast.error(t('modals.addPet.photoTooLarge', { size: fileSizeMB, max: 50 }));
        return;
      }
      
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const toggleDateInputType = () => {
    setDateInputType(prev => prev === 'calendar' ? 'manual' : 'calendar');
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setBirthDateObj(date);
      setBirthDate(format(date, 'yyyy-MM-dd'));
      setCalendarDate(date);
    }
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBirthDate(value);
    if (value) {
      const date = new Date(value + 'T00:00:00');
      if (!isNaN(date.getTime())) {
        setBirthDateObj(date);
        setCalendarDate(date);
      } else {
        setBirthDateObj(null);
      }
    } else {
      setBirthDateObj(null);
    }
  };

  const navigateCalendar = (direction: 'prev' | 'next', unit: 'month' | 'year') => {
    const newDate = new Date(calendarDate);
    if (unit === 'month') {
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
    } else {
      if (direction === 'prev') {
        newDate.setFullYear(newDate.getFullYear() - 1);
      } else {
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
    }
    setCalendarDate(newDate);
  };

  // ✅ NEW: Handle species change with breed reset
  const handleSpeciesChange = (species: Species) => {
    setSelectedSpecies(species);
    setSelectedSpeciesId(species.id);
    setSelectedBreed(null); // Reset breed when species changes
  };
  
  // Handle location detection
  const handleDetectLocation = async () => {
    const detectedLocation = await detectLocation();
    if (detectedLocation) {
      setLocation(detectedLocation.location);
      setHasCustomLocation(true);
      toast.success(t('location.detected') || 'Location detected successfully');
    } else if (geolocationError) {
      toast.error(geolocationError);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!petId) return;

    if (!name.trim() || !selectedSpecies) {
      toast.error(t('modals.addPet.requiredFields'));
      return;
    }

    // Validate weight if provided
    if (weight && (isNaN(Number(weight)) || Number(weight) <= 0 || Number(weight) > 1500)) {
      toast.error(t('modals.addPet.weightError'));
      return;
    }
    
    const traits = personalityTraits
      .split(',')
      .map(trait => trait.trim())
      .filter(trait => trait !== '');
    
    // ✅ NEW: Build update data with both breed_id and breed for compatibility
    const petData = {
      name: name.trim(),
      species: selectedSpecies.code, // Use species code
      breed: selectedBreed?.name || undefined, // Keep breed text for compatibility
      breed_id: selectedBreed?.id || undefined,
      sex: sex as 'male' | 'female' | 'not_specified',
      weight_kg: weight ? Number(weight) : undefined,
      color: color.trim() || undefined,
      birth_date: birthDate || undefined,
      is_memorial: isMemorial,
      death_date: isMemorial && deathDate ? deathDate : null,
      personality_traits: traits.length > 0 ? traits : undefined,
      photo: photoFile || undefined,
      location: hasCustomLocation ? location.trim() || undefined : undefined,
      // New compatibility fields
      energy_level: energyLevel || undefined,
      size_category: sizeCategory || undefined,
      is_neutered: isNeutered ?? undefined,
      socialization_level: socializationLevel || undefined,
      vaccination_status: vaccinationStatus || undefined
    };
    
    try {
      await updatePet({ 
        petId, 
        petData 
      });

      // Sync weight with weight tracker: create a new record if weight changed
      const currentWeight = latestWeight ? Number(latestWeight.weight_kg) : (pet?.weight_kg ? Number(pet.weight_kg) : null);
      const newWeight = weight ? Number(weight) : null;
      if (newWeight && newWeight !== currentWeight) {
        await addRecord.mutateAsync({
          weight_kg: newWeight,
          measurement_date: new Date().toISOString().split('T')[0],
          notes: 'Updated from Edit Pet form',
        });
      }
      
      toast.success(t('common.saveSuccess') || "Pet updated successfully!");
      navigate(`/pet/${petId}`);
    } catch (error: any) {
      toast.error(`${t('common.error')}: ${error.message || t('common.unknownError')}`);
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Skeleton className="h-7 w-40" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }
  
  if (!pet) {
    return (
      <div className="p-4">
        <div className="text-center">
          <p className="text-red-500 mb-2">Pet not found</p>
          <Button onClick={() => navigate('/my-pets')}>Back to My Pets</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 pb-24 max-w-md mx-auto">
      <div className="flex items-center mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-medium">{t('titles.editPet')} {pet?.name}</h1>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col items-center mb-4">
          <div className="relative mb-2">
            <Avatar className="h-24 w-24">
              {photoPreview ? (
                <AvatarImage src={photoPreview} alt={name} />
              ) : pet?.photo_url ? (
                <AvatarImage src={pet.photo_url} alt={pet.name} />
              ) : (
                <AvatarFallback>{name.charAt(0) || (pet?.name?.charAt(0) || '')}</AvatarFallback>
              )}
            </Avatar>
          </div>
          
          <Label 
            htmlFor="photo" 
            className="bg-pet-purple text-white px-3 py-1 rounded-md cursor-pointer text-sm"
          >
            {t('modals.addPet.changePhoto')}
          </Label>
          <Input 
            id="photo"
            type="file" 
            accept="image/*"
            className="hidden" 
            onChange={handlePhotoChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">{t('modals.addPet.petName')}*</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="species">{t('modals.addPet.species')}*</Label>
          {isLoadingSpecies ? (
            <Skeleton className="h-10 w-full" />
          ) : speciesError ? (
            <div className="text-sm text-red-500">Error loading species: {speciesError}</div>
          ) : (
            <Select 
              value={selectedSpecies?.id.toString() || ''} 
              onValueChange={(value) => {
                const species = speciesList.find(s => s.id.toString() === value);
                if (species) handleSpeciesChange(species);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectSpecies') || "Select pet species"} />
              </SelectTrigger>
              <SelectContent>
                {speciesList.map((species) => (
                  <SelectItem key={species.id} value={species.id.toString()}>
                    {species.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        <BreedAutocomplete
          breeds={breeds}
          selectedBreed={selectedBreed}
          onBreedSelect={setSelectedBreed}
          disabled={!selectedSpecies || isLoadingBreeds}
          loading={isLoadingBreeds}
          placeholder={
            !selectedSpecies 
              ? t('modals.addPet.selectSpeciesFirst') || "First select a species"
              : isLoadingBreeds 
                ? t('common.loading') || "Loading breeds..."
                : t('modals.addPet.selectBreed') || "Search breeds..."
          }
        />
        
        {breedsError && (
          <div className="text-sm text-red-500">Error loading breeds: {breedsError}</div>
        )}

        <div className="space-y-2">
          <Label htmlFor="sex">{t('modals.addPet.sex')}</Label>
          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger>
              <SelectValue placeholder={t('modals.addPet.selectSex')} />
            </SelectTrigger>
            <SelectContent>
              {petSexOptions.map((sexOption) => (
                <SelectItem key={sexOption.value} value={sexOption.value}>
                  {sexOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="weight">{t('modals.addPet.weight')}</Label>
          <Input
            id="weight"
            type="number"
            step="0.01"
            min="0.01"
            max="1500"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder={t('modals.addPet.weightPlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="color">{t('modals.addPet.color')}</Label>
          <Input
            id="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            placeholder={t('modals.addPet.colorPlaceholder')}
          />
        </div>

        {/* Location section with smart default */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="location">{t('modals.addPet.location')}</Label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="hasCustomLocation"
                checked={hasCustomLocation}
                onChange={(e) => {
                  setHasCustomLocation(e.target.checked);
                  if (!e.target.checked) {
                    setLocation(user?.user_metadata?.location || '');
                  }
                }}
                className="w-3 h-3"
              />
              <Label htmlFor="hasCustomLocation" className="text-xs text-muted-foreground">
                {t('modals.addPet.customLocation') || 'Different from my location'}
              </Label>
            </div>
          </div>
          
          {hasCustomLocation ? (
            <div className="flex gap-2">
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={t('modals.addPet.locationPlaceholder') || 'Enter pet location'}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDetectLocation}
                disabled={isDetecting}
                className="shrink-0"
              >
                <Navigation className="h-4 w-4" />
                {isDetecting ? "..." : "GPS"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm text-muted-foreground">
              <span>{t('modals.addPet.usingOwnerLocation') || 'Using owner location:'}</span>
              <span>{user?.user_metadata?.location || t('modals.addPet.noLocationSet') || 'No location set'}</span>
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="birth_date">{t('modals.addPet.birthDate')}</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleDateInputType}
              className="h-6 text-xs text-muted-foreground"
            >
              {dateInputType === 'calendar' ? t('modals.addPet.enterManually') : t('modals.addPet.useCalendar')}
            </Button>
          </div>
          
          {dateInputType === 'calendar' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !birthDateObj && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {birthDateObj ? format(birthDateObj, "PPP") : t('modals.addPet.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                  {/* Year/Month Navigation */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateCalendar('prev', 'year')}
                      >
                        <ChevronLeft className="h-3 w-3" />
                        <ChevronLeft className="h-3 w-3 -ml-1" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateCalendar('prev', 'month')}
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm font-medium">
                      {format(calendarDate, "MMMM yyyy")}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateCalendar('next', 'month')}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigateCalendar('next', 'year')}
                      >
                        <ChevronRight className="h-3 w-3" />
                        <ChevronRight className="h-3 w-3 -ml-1" />
                      </Button>
                    </div>
                  </div>
                  <Calendar
                    mode="single"
                    selected={birthDateObj || undefined}
                    onSelect={handleDateSelect}
                    month={calendarDate}
                    onMonthChange={setCalendarDate}
                    disabled={(date) =>
                      date > new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                    className={cn("pointer-events-auto")}
                  />
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Input
              id="birth_date"
              type="date"
              value={birthDate}
              onChange={handleDateInput}
              max={format(new Date(), "yyyy-MM-dd")}
              min="1900-01-01"
            />
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="personality_traits">{t('modals.addPet.personalityTraits')}</Label>
          <Textarea
            id="personality_traits"
            value={personalityTraits}
            onChange={(e) => setPersonalityTraits(e.target.value)}
            placeholder={t('modals.addPet.personalityPlaceholder')}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="energy_level">{t('modals.addPet.energyLevel')}</Label>
          <Select value={energyLevel} onValueChange={setEnergyLevel}>
            <SelectTrigger>
              <SelectValue placeholder={t('modals.addPet.selectEnergyLevel')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="very_low">{t('modals.energyLevels.veryLow')}</SelectItem>
              <SelectItem value="low">{t('modals.energyLevels.low')}</SelectItem>
              <SelectItem value="moderate">{t('modals.energyLevels.moderate')}</SelectItem>
              <SelectItem value="high">{t('modals.energyLevels.high')}</SelectItem>
              <SelectItem value="very_high">{t('modals.energyLevels.veryHigh')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="size_category">{t('modals.addPet.sizeCategory')}</Label>
          <Select value={sizeCategory} onValueChange={setSizeCategory}>
            <SelectTrigger>
              <SelectValue placeholder={t('modals.addPet.selectSizeCategory')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="toy">{t('modals.sizeCategories.toy')}</SelectItem>
              <SelectItem value="small">{t('modals.sizeCategories.small')}</SelectItem>
              <SelectItem value="medium">{t('modals.sizeCategories.medium')}</SelectItem>
              <SelectItem value="large">{t('modals.sizeCategories.large')}</SelectItem>
              <SelectItem value="giant">{t('modals.sizeCategories.giant')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="socialization_level">{t('modals.addPet.socializationLevel')}</Label>
          <Select value={socializationLevel} onValueChange={setSocializationLevel}>
            <SelectTrigger>
              <SelectValue placeholder={t('modals.addPet.selectSocializationLevel')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shy">{t('modals.socializationLevels.shy')}</SelectItem>
              <SelectItem value="moderate">{t('modals.socializationLevels.moderate')}</SelectItem>
              <SelectItem value="very_social">{t('modals.socializationLevels.verySocial')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="vaccination_status">{t('modals.addPet.vaccinationStatus')}</Label>
          <Select value={vaccinationStatus} onValueChange={setVaccinationStatus}>
            <SelectTrigger>
              <SelectValue placeholder={t('modals.addPet.selectVaccinationStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="up_to_date">{t('modals.vaccinationStatus.upToDate')}</SelectItem>
              <SelectItem value="partial">{t('modals.vaccinationStatus.partial')}</SelectItem>
              <SelectItem value="unknown">{t('modals.vaccinationStatus.unknown')}</SelectItem>
              <SelectItem value="none">{t('modals.vaccinationStatus.none')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2 py-2">
          <Switch
            id="is_neutered"
            checked={isNeutered === true}
            onCheckedChange={(checked) => setIsNeutered(checked)}
          />
          <Label htmlFor="is_neutered">{t('modals.addPet.neutered')}</Label>
        </div>
        
        <div className="flex items-center space-x-2 py-2">
          <Switch
            id="is_memorial"
            checked={isMemorial}
            onCheckedChange={setIsMemorial}
          />
          <Label htmlFor="is_memorial">{t('modals.addPet.memorial')}</Label>
        </div>
        
        {/* Death date - only shown when memorial is enabled */}
        {isMemorial && (
          <div className="space-y-2">
            <Label htmlFor="death_date">{t('modals.addPet.deathDate')}</Label>
            <Input
              id="death_date"
              type="date"
              value={deathDate}
              onChange={(e) => {
                const value = e.target.value;
                setDeathDate(value);
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  if (!isNaN(date.getTime())) {
                    setDeathDateObj(date);
                  } else {
                    setDeathDateObj(null);
                  }
                } else {
                  setDeathDateObj(null);
                }
              }}
              placeholder={t('modals.addPet.deathDatePlaceholder')}
              max={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>
        )}
        
        <Button 
          type="submit" 
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={isUpdating}
        >
          {isUpdating ? t('common.loading') || "Updating..." : t('common.save')}
        </Button>
      </form>
    </div>
  );
}

